import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { stripeService, markDeviceTrialStarted } from '../services/stripe';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';
import './UpgradeToProModal.css';

/**
 * The upgrade prompt shown when a Free user reaches a limit — opens, searches, reinventions,
 * ideas — or the API refuses something for want of a plan (see GlobalUpgradePrompt).
 *
 * Both paid plans side by side under one Monthly/Yearly toggle, each going straight to its own
 * Stripe Checkout. Premium used to be absent here, which left the plan that earns the most
 * reachable only from the plans page. Sized to fit a laptop screen without scrolling.
 *
 * Props:
 *  - isOpen:   whether the modal is visible
 *  - onClose:  called when the user dismisses the paywall
 *  - subtitle: lead paragraph under the title (defaults to the reinvention-limit copy).
 *              Pass a different string for other limits (e.g. saved patents).
 *  - onBeforeUpgrade: optional callback run right before redirecting to Stripe — lets the
 *              caller stash any in-progress state (e.g. a draft) so it survives the round-trip.
 *  - note:     optional small muted line under the subtitle (e.g. "resets tomorrow").
 *
 * The filing line names the one thing that was never behind a subscription: $299 files a
 * patent on any plan. Most filings have come from free accounts, and a prompt that only sells
 * plans hides the bigger sale.
 */
// Paywall lead-copy per limit type — only the first sentence differs.
export const IMPROVEMENT_PAYWALL_SUBTITLE =
  'You’ve used your free patent reinvention. Upgrade to Pro to save unlimited ideas and keep your best concepts organized.';
export const SAVES_PAYWALL_SUBTITLE =
  'You’ve used all your patent saves. Upgrade to Pro to save unlimited ideas and keep your best concepts organized.';
export const IDEAS_PAYWALL_SUBTITLE =
  'You’ve used all your idea saves. Upgrade to Pro to save unlimited ideas and keep your best concepts organized.';
export const VIEWS_PAYWALL_SUBTITLE =
  'You’ve hit your limit on abandoned patents for the day.';
export const CHATBOT_PAYWALL_SUBTITLE =
  'You’ve used your free chatbot session.';
export const REINVENT_PAYWALL_SUBTITLE =
  'You’ve used your free reinvention. Pro includes eight a month. Your wall is here every day regardless.';
// Each of these names what is still free rather than only what is blocked. See
// handoff/explorer/README.md §6 — a limit should not read as a closed door.
export const OPENS_PAYWALL_SUBTITLE =
  'You’ve opened everything you can today. Saving stays unlimited, and a new wall arrives at 8:00am.';
export const SEARCH_PAYWALL_SUBTITLE =
  'Search is a Pro tool. You’ve used today’s free search. Your wall is here every day regardless.';
// For a refusal that did not come from a specific limit — see GlobalUpgradePrompt.
export const GENERIC_PAYWALL_SUBTITLE =
  'That one is a Pro feature. Everything on the free plan is still yours to use.';
export const VIEWS_PAYWALL_NOTE =
  'Your limit resets tomorrow. You can still open and search patents you’ve already viewed today.';

const PRO_POINTS = ['Unlimited opens and searches', '8 reinventions a month', 'Unlimited ideas saved'];
const PREMIUM_POINTS = ['Competitor monitoring, checked daily', 'Watch up to 25 competitors', 'Email alerts on new filings and launches'];

function UpgradeToProModal({ isOpen, onClose, subtitle = IMPROVEMENT_PAYWALL_SUBTITLE, onBeforeUpgrade, note }) {
  // 'annual' | 'monthly' — yearly first, as on the plans page
  const [billingCycle, setBillingCycle] = useState('annual');
  // Which plan's checkout is starting, so only that button says so and both stay disabled.
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { subscriptionData } = useSubscription();

  if (!isOpen) return null;

  const isYearly = billingCycle === 'annual';
  // Premium is the only plan with a trial, and an account that has had one is charged today.
  // Same rule, same field, as the plans page. See TRIAL_PLANS in checkout.service.ts.
  const premiumTrial = !subscriptionData?.trialStartedAt;

  const startFiling = () => {
    onClose?.();
    navigate('/new-patent', { state: { freshStart: true, filingSource: 'upgrade_prompt' } });
  };

  const handleUpgrade = async (plan) => {
    if (pending) return;
    setPending(plan);
    setError(null);
    try {
      // Let the caller stash any in-progress state before we leave the page for Stripe.
      if (onBeforeUpgrade) onBeforeUpgrade();
      const response = await stripeService.createSubscriptionSession(
        plan,
        billingCycle,
        `${window.location.origin}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        window.location.href, // return here on cancel
      );
      if (!response?.url) throw new Error('Could not start checkout. Please try again.');
      if (plan === 'business') markDeviceTrialStarted();
      window.location.href = response.url;
    } catch (err) {
      console.error(`Failed to start ${plan} checkout:`, err);
      setError(err.data?.error || err.message || 'Something went wrong starting checkout. Please try again.');
      setPending(null);
    }
  };

  return (
    <div className="uptp-overlay" onClick={onClose}>
      <div
        className="uptp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="uptp-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="uptp-header">
          <div className="uptp-header-top">
            <div className="uptp-badge">
              <LockIcon />
              <span>Free plan limit reached</span>
            </div>
            <button className="uptp-close" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </button>
          </div>
          <div className="uptp-heading">
            <p id="uptp-title" className="uptp-title">Keep going with a plan</p>
            <p className="uptp-subtitle">{subtitle}</p>
            {note && <p className="uptp-note">{note}</p>}
          </div>
        </div>

        <div className="uptp-body">
          <div className="uptp-toggle" role="tablist" aria-label="Billing cycle">
            <button
              className={`uptp-toggle-btn ${!isYearly ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
              role="tab"
              aria-selected={!isYearly}
            >
              Monthly
            </button>
            <button
              className={`uptp-toggle-btn ${isYearly ? 'active' : ''}`}
              onClick={() => setBillingCycle('annual')}
              role="tab"
              aria-selected={isYearly}
            >
              Yearly · <span className="uptp-toggle-accent">save up to 36%</span>
            </button>
          </div>

          <div className="uptp-plans">
            {/* Pro */}
            <section className="uptp-plan-card">
              <p className="uptp-plan-name uptp-plan-name-pro">Pro</p>
              <div className="uptp-price-row">
                {isYearly && <span className="uptp-price-strike">$39</span>}
                <span className="uptp-price-amount">{isYearly ? '$25' : '$39'}</span>
                <span className="uptp-price-per">/ mo</span>
              </div>
              <ul className="uptp-points">
                {PRO_POINTS.map((f) => <li key={f}><CheckIcon />{f}</li>)}
              </ul>
              <button
                className="uptp-cta uptp-cta-pro"
                onClick={() => handleUpgrade('pro')}
                disabled={Boolean(pending)}
              >
                {pending === 'pro' ? 'Redirecting…' : 'Upgrade to Pro'}
              </button>
            </section>

            {/* Premium — teal is the monitoring feature's colour throughout the app. */}
            <section className="uptp-plan-card uptp-plan-card-premium">
              <p className="uptp-plan-name uptp-plan-name-premium">Premium</p>
              <div className="uptp-price-row">
                {isYearly && <span className="uptp-price-strike">$99</span>}
                <span className="uptp-price-amount">{isYearly ? '$69' : '$99'}</span>
                <span className="uptp-price-per">/ mo</span>
              </div>
              <ul className="uptp-points uptp-points-premium">
                <li className="uptp-points-lead">Everything in Pro, plus:</li>
                {PREMIUM_POINTS.map((f) => <li key={f}><CheckIcon />{f}</li>)}
              </ul>
              <button
                className="uptp-cta uptp-cta-premium"
                onClick={() => handleUpgrade('business')}
                disabled={Boolean(pending)}
              >
                {pending === 'business' ? 'Redirecting…' : premiumTrial ? 'Start 3-day free trial' : 'Subscribe'}
              </button>
            </section>
          </div>

          {error && <p className="uptp-error">{error}</p>}

          <div className="uptp-filing">
            <p className="uptp-filing-text">
              <strong>Ready to protect an idea?</strong> Filing a patent is $299 on any plan,
              Free included. No subscription needed.
            </p>
            <button type="button" className="uptp-filing-link" onClick={startFiling}>
              Start a filing →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- Inline icons (Feather-style, matching the app's existing convention) --- */

function LockIcon() {
  return (
    <svg className="uptp-lock-icon" width="14" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="uptp-check-icon" width="19" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default UpgradeToProModal;
